import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Calendar, Tag } from "lucide-react";
import Navbar from "@/components/portfolio/Navbar";
import Contact from "@/components/portfolio/Contact";
import api from "@/lib/api";

export default function WorkDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [all, setAll] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    let active = true;
    api
      .get(`/projects/${id}`)
      .then(({ data }) => {
        if (!active) return;
        setProject(data);
        document.title = `${data.title} — Ali Renan Uyanık`;
      })
      .catch(() => active && setNotFound(true));

    api
      .get("/projects")
      .then(({ data }) => active && setAll(data))
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [id]);

  if (notFound) {
    return (
      <main className="min-h-screen bg-black text-white">
        <Navbar />
        <section className="pt-40 pb-32 max-w-3xl mx-auto px-6 text-center">
          <div className="text-sm text-zinc-500">404</div>
          <h1 className="font-heading mt-3 text-4xl md:text-5xl font-light tracking-tight">
            We couldn&apos;t find that project.
          </h1>
          <button
            onClick={() => navigate("/#work")}
            className="mt-8 inline-flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-full text-sm font-medium hover:scale-[1.04] transition-transform"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={2} />
            Back to selected work
          </button>
        </section>
      </main>
    );
  }

  if (!project) {
    return (
      <main className="min-h-screen bg-black text-white">
        <Navbar />
        <section className="pt-40 pb-32 max-w-3xl mx-auto px-6 text-center text-zinc-500 text-sm">
          Loading project…
        </section>
      </main>
    );
  }

  const more = all.filter((p) => p.id !== project.id).slice(0, 2);
  const blocks = Array.isArray(project.sections) ? project.sections : [];
  const paragraphs = (project.body || project.summary || "")
    .split(/\n\n+/)
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <main
      data-testid="work-detail-root"
      className="relative min-h-screen bg-black text-white"
    >
      <Navbar />

      <article className="relative pt-28 md:pt-32 pb-12 md:pb-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Link
            to="/#work"
            data-testid="work-back"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
            Selected work
          </Link>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="mt-8 flex items-center gap-3 text-sm text-zinc-500"
          >
            <span className="inline-flex items-center gap-1.5">
              <Tag className="w-3.5 h-3.5" strokeWidth={1.5} />
              {project.tag}
            </span>
            <span className="w-1 h-1 rounded-full bg-zinc-700" />
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />
              {project.year}
            </span>
          </motion.div>

          <motion.h1
            data-testid="work-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="font-heading mt-4 text-4xl md:text-6xl lg:text-[68px] font-light tracking-[-0.03em] leading-[1.02] text-white max-w-4xl"
          >
            {project.title}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-10 md:mt-14 relative aspect-[16/9] overflow-hidden rounded-2xl border border-white/10"
          >
            {project.image && (
              <img
                src={project.image}
                alt={project.title}
                className="absolute inset-0 h-full w-full object-cover"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          </motion.div>

          <div className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-10 lg:gap-16">
            <div>
              {blocks.length > 0 ? (
                <div className="max-w-none">
                  {blocks.map((b, i) => (
                    <div key={i} className="mt-10 first:mt-0">
                      {b.text && (
                        <motion.p
                          initial={{ opacity: 0, y: 12 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true, margin: "-40px" }}
                          transition={{ duration: 0.6 }}
                          className="text-zinc-300 font-light text-base md:text-lg leading-[1.85]"
                          data-testid={`work-paragraph-${i}`}
                        >
                          {b.text}
                        </motion.p>
                      )}
                      {b.image && (
                        <motion.figure
                          initial={{ opacity: 0, y: 16 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true, margin: "-40px" }}
                          transition={{ duration: 0.6, delay: 0.05 }}
                          className="mt-6 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]"
                          data-testid={`work-figure-${i}`}
                        >
                          <div className="relative overflow-hidden bg-black">
                            <img
                              src={b.image}
                              alt={b.caption || project.title}
                              loading="lazy"
                              className="w-full h-auto object-contain"
                            />
                          </div>
                          {b.caption && (
                            <figcaption className="px-5 py-3.5 text-sm text-zinc-400 font-light border-t border-white/5">
                              {b.caption}
                            </figcaption>
                          )}
                        </motion.figure>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="prose prose-invert max-w-none">
                  {paragraphs.map((p, i) => (
                    <motion.p
                      key={i}
                      initial={{ opacity: 0, y: 12 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ duration: 0.6, delay: i * 0.04 }}
                      className="text-zinc-300 font-light text-base md:text-lg leading-[1.85] mt-6 first:mt-0"
                      data-testid={`work-paragraph-${i}`}
                    >
                      {p}
                    </motion.p>
                  ))}
                </div>
              )}
            </div>

            <aside className="lg:sticky lg:top-32 self-start space-y-6">
              <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6">
                <div className="text-sm text-zinc-500">Stack</div>
                <ul className="mt-3 flex flex-wrap gap-2">
                  {(project.stack || []).map((s) => (
                    <li
                      key={s}
                      className="text-xs text-zinc-300 bg-white/[0.03] border border-white/10 px-2.5 py-1 rounded-full"
                    >
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6">
                <div className="text-sm text-zinc-500">At a glance</div>
                <dl className="mt-3 space-y-2 text-sm">
                  <Row label="Role" value={project.tag} />
                  <Row label="Year" value={project.year} />
                  <Row label="Status" value={statusFromTag(project.tag, project.year)} />
                </dl>
              </div>
              {(project.contributions || []).length > 0 && (
                <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6">
                  <div className="text-sm text-zinc-500">My role</div>
                  <ul className="mt-3 space-y-2.5">
                    {project.contributions.map((c, i) => (
                      <li
                        key={i}
                        className="flex gap-2.5 text-sm text-zinc-300 font-light leading-relaxed"
                        data-testid={`work-contribution-${i}`}
                      >
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-blue-400" />
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </aside>
          </div>

          {blocks.length === 0 && (project.gallery || []).length > 0 && (
            <div className="mt-16 md:mt-20">
              <div className="flex items-center gap-3 text-sm text-zinc-500">
                <span className="w-8 h-px bg-zinc-700" />
                <span>Gallery</span>
              </div>
              <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
                {project.gallery.map((g, i) => (
                  <motion.figure
                    key={g.src || i}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.6, delay: (i % 2) * 0.05 }}
                    className="group overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]"
                    data-testid={`work-gallery-${i}`}
                  >
                    <div className="relative aspect-[16/10] overflow-hidden bg-black">
                      <img
                        src={g.src}
                        alt={g.caption || project.title}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-contain p-2 transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
                      />
                    </div>
                    {g.caption && (
                      <figcaption className="px-5 py-4 text-sm text-zinc-400 font-light border-t border-white/5">
                        {g.caption}
                      </figcaption>
                    )}
                  </motion.figure>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>

      {more.length > 0 && (
        <section className="py-16 md:py-20 bg-[#050505]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="flex items-center gap-3 text-sm text-zinc-500 mb-6">
              <span className="w-8 h-px bg-zinc-700" />
              <span>More work</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {more.map((p) => (
                <Link
                  key={p.id}
                  to={`/work/${p.id}`}
                  data-testid={`work-more-${p.id}`}
                  className="group block rounded-2xl border border-white/10 bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.04] transition-colors overflow-hidden"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    {p.image && (
                      <img
                        src={p.image}
                        alt={p.title}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover grayscale-[55%] group-hover:grayscale-0 scale-[1.02] group-hover:scale-105 transition-all duration-[1200ms] ease-out"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                  </div>
                  <div className="p-6 flex items-start justify-between gap-4">
                    <div>
                      <div className="text-sm text-zinc-500">
                        {p.tag} · {p.year}
                      </div>
                      <h3 className="font-heading mt-2 text-xl md:text-2xl font-medium tracking-tight text-white">
                        {p.title}
                      </h3>
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-zinc-400 group-hover:text-white transition-colors mt-1.5" strokeWidth={1.75} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Contact />
    </main>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-zinc-500">{label}</dt>
      <dd className="text-zinc-200 text-right">{value || "—"}</dd>
    </div>
  );
}

function statusFromTag(tag, year) {
  const txt = `${tag || ""} ${year || ""}`.toLowerCase();
  if (txt.includes("present") || txt.includes("in progress")) return "In progress";
  return "Shipped";
}
